// Пользователи (contracts/api.md, «Пользователи»).
import { z } from '../kernel/zod.ts';
import { AreasInputSchema, areasFitRole } from '../access/area.ts';
import {
  EmailInputSchema,
  FullNameInputSchema,
  UserListItemSchema,
  UserSchema,
  UserStateSchema,
} from '../access/user.ts';
import { IdSchema } from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { PageQuerySchema, pageSchema, sortSchema } from '../kernel/page.ts';
import { RoleInputSchema, RoleSchema } from '../rights/roles.ts';

export const userSortFields = ['fullName', 'email', 'createdAt'] as const;

/**
 * GET /api/users: поиск по ФИО и почте, фильтры, сортировка, курсор. Без state — все, кроме архива.
 * fullName — пользователи с тем же ФИО, действующие и в архиве: подсказка при создании (T126);
 * сравнение без учёта регистра, «ё» как «е», пробелы и точки — разделители.
 */
export const UsersQuerySchema = z.object({
  ...PageQuerySchema.shape,
  q: z.string().trim().max(200).optional(),
  fullName: z.string().trim().min(1).max(200).optional(),
  role: RoleSchema.optional(),
  state: UserStateSchema.optional(),
  sort: sortSchema(userSortFields).default('fullName'),
});
export type UsersQuery = z.input<typeof UsersQuerySchema>;

export const UsersPageSchema = pageSchema(UserListItemSchema);

/**
 * Роль и области нового назначения; для администратора клиента и сопровождения — только установка.
 * Лишние поля, в том числе срок, отклоняются (REC-007), как у POST /api/assignments: срока
 * у собственного назначения нет (FR-014а).
 */
export const NewAssignmentSchema = z
  .strictObject({ role: RoleInputSchema, areas: AreasInputSchema })
  .refine((v) => areasFitRole(v.role, v.areas), {
    path: ['areas'],
    error: fieldMessages.areasInstallationOnly,
  });

/** POST /api/users. */
export const CreateUserBodySchema = z.object({
  email: EmailInputSchema,
  fullName: FullNameInputSchema,
  assignments: z
    .array(NewAssignmentSchema, { error: fieldMessages.role })
    .min(1, { error: fieldMessages.role }),
});
export type CreateUserBody = z.input<typeof CreateUserBodySchema>;

/**
 * 201 (035, FR-008): ссылки нет — её выпускает приветственное письмо при отправке; letterId — номер
 * письма (номер события user.welcome_requested), состояние — GET /api/users/{id}/welcome.
 */
export const CreateUserResponseSchema = z.object({
  user: UserSchema,
  welcome: z.object({ letterId: IdSchema }),
});
export type CreateUserResponse = z.infer<typeof CreateUserResponseSchema>;

/** PATCH /api/users/{id} с If-Match; смена почты отзывает все сеансы. */
export const UpdateUserBodySchema = z.object({
  fullName: FullNameInputSchema.optional(),
  email: EmailInputSchema.optional(),
});
export type UpdateUserBody = z.input<typeof UpdateUserBodySchema>;
